import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Sidebar } from "./components/Sidebar";
import { Splash } from "./components/Splash";
import { AlertsProvider } from "./components/AlertsProvider";
import {
  ShellContext,
  NARROW_QUERY,
  type ShellState,
} from "./components/ShellContext";
import { HomePage } from "./pages/HomePage";
import { SkeletonRows } from "./components/ds/SkeletonRows";
import { parseRoute, pushRoute, type Page } from "./routes";
import { useEnterAnimation } from "./lib/useEnterAnimation";
import { ThemeOverrideProvider } from "./lib/theme";
import { ui } from "./ui";
import { settingsPageStyle } from "./pages/settingsChrome";

/**
 * Every page but Home loads on demand, so the first paint does not carry
 * three.js, markdown and every screen. PAGES is also the idle prefetch list,
 * so a later click finds its chunk already in the cache.
 */
const PAGES = {
  DrawingsPage: () => import("./pages/DrawingsPage"),
  ProfilePage: () => import("./pages/ProfilePage"),
  AiPage: () => import("./pages/AiPage"),
  AgentsPage: () => import("./pages/AgentsPage"),
  IntegrationsPage: () => import("./pages/IntegrationsPage"),
  RfqPage: () => import("./pages/RfqPage"),
  CustomersPage: () => import("./pages/CustomersPage"),
  CustomerDetailPage: () => import("./pages/CustomerDetailPage"),
  RfqDetailPage: () => import("./pages/RfqDetailPage"),
  RfqIntakePage: () => import("./pages/RfqIntakePage"),
  ErpPage: () => import("./pages/ErpPage"),
  ProductionPage: () => import("./pages/ProductionPage"),
  ProductionDetailPage: () => import("./pages/ProductionDetailPage"),
  JobWorkspace: () => import("./pages/JobWorkspace"),
  NotFoundPage: () => import("./pages/NotFoundPage"),
};
const DrawingsPage = lazy(() => PAGES.DrawingsPage().then((m) => ({ default: m.DrawingsPage })));
const ProfilePage = lazy(() => PAGES.ProfilePage().then((m) => ({ default: m.ProfilePage })));
const AiPage = lazy(() => PAGES.AiPage().then((m) => ({ default: m.AiPage })));
const AgentsPage = lazy(() => PAGES.AgentsPage().then((m) => ({ default: m.AgentsPage })));
const IntegrationsPage = lazy(() => PAGES.IntegrationsPage().then((m) => ({ default: m.IntegrationsPage })));
const RfqPage = lazy(() => PAGES.RfqPage().then((m) => ({ default: m.RfqPage })));
const CustomersPage = lazy(() => PAGES.CustomersPage().then((m) => ({ default: m.CustomersPage })));
const CustomerDetailPage = lazy(() => PAGES.CustomerDetailPage().then((m) => ({ default: m.CustomerDetailPage })));
const RfqDetailPage = lazy(() => PAGES.RfqDetailPage().then((m) => ({ default: m.RfqDetailPage })));
const RfqIntakePage = lazy(() => PAGES.RfqIntakePage().then((m) => ({ default: m.RfqIntakePage })));
const ErpPage = lazy(() => PAGES.ErpPage().then((m) => ({ default: m.ErpPage })));
const ProductionPage = lazy(() => PAGES.ProductionPage().then((m) => ({ default: m.ProductionPage })));
const ProductionDetailPage = lazy(() => PAGES.ProductionDetailPage().then((m) => ({ default: m.ProductionDetailPage })));
const JobWorkspace = lazy(() => PAGES.JobWorkspace().then((m) => ({ default: m.JobWorkspace })));
const NotFoundPage = lazy(() => PAGES.NotFoundPage().then((m) => ({ default: m.NotFoundPage })));

function prefetchPages() {
  for (const load of Object.values(PAGES)) void load().catch(() => {});
}

function matchesNarrow(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(NARROW_QUERY).matches;
}

/**
 * Product shell. RootApp mounts it for every path. A `/v2` bookmark still
 * opens the same page.
 *
 * Each page owns its own topbar, so the title, subtitle and search box can be
 * worded for the module it belongs to.
 */
export function App() {
  const [route, setRoute] = useState(() => parseRoute());

  useEffect(() => {
    const sync = () => setRoute(parseRoute());
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);

  // v1 owns <body> and defaults to a dark ground; claim it back while v2 is up.
  useEffect(() => {
    document.body.classList.add("sc-active");
    return () => document.body.classList.remove("sc-active");
  }, []);

  // Brand the first paint rather than flashing an empty shell.
  const [booted, setBooted] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setBooted(true), 350);
    return () => window.clearTimeout(id);
  }, []);

  // After the first screen is up, fetch the other pages while the browser is idle.
  useEffect(() => {
    if (!booted) return;
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(prefetchPages, { timeout: 3000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(prefetchPages, 1500);
    return () => window.clearTimeout(id);
  }, [booted]);

  // Wide: icon rail only. Narrow: labelled drawer (closed by default).
  const [narrow, setNarrow] = useState(matchesNarrow);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(NARROW_QUERY);
    const sync = () => {
      setNarrow(mq.matches);
      if (!mq.matches) setDrawerOpen(false);
    };
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const sidebarOpen = narrow && drawerOpen;

  const toggleSidebar = useCallback(() => {
    if (!narrow) return;
    setDrawerOpen((v) => !v);
  }, [narrow]);

  const openSidebar = useCallback(() => {
    if (!narrow) return;
    setDrawerOpen(true);
  }, [narrow]);

  const closeSidebar = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  // Escape closes the drawer, like any other overlay.
  useEffect(() => {
    if (!(narrow && drawerOpen)) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [narrow, drawerOpen]);

  const navigate = useCallback(
    (page: Page, id?: string | null, sub?: string | null) => {
      pushRoute(page, id, sub);
      setRoute({ page, id: id ?? null, sub: sub ?? null });
      // Picking a destination is what a drawer is for; it can go now.
      setDrawerOpen(false);
    },
    [],
  );

  // A new module or a new record is a new screen; a tab inside one is not, so
  // panes with a standing 3D preview do not blink when their tab changes.
  const mainRef = useEnterAnimation<HTMLElement>(
    [route.page, route.id],
    ":scope > *:not(.sc-topbar)",
  );

  const shellState = useMemo<ShellState>(
    () => ({ narrow, sidebarOpen, toggleSidebar, openSidebar, closeSidebar }),
    [narrow, sidebarOpen, toggleSidebar, openSidebar, closeSidebar],
  );

  function renderPage() {
    const { page, id, sub } = route;
    if (page === "home") return <HomePage onNavigate={navigate} />;
    if (page === "drawings") {
      // One job is a workspace: the preview beside BOM, review or a part.
      // `review` is a named view of the job; anything else is a BOM line number.
      if (id) {
        const view =
          sub === "review"
            ? ({ kind: "review" } as const)
            : sub === "cost"
              ? ({ kind: "cost" } as const)
            : sub
              ? ({ kind: "part", itemNo: sub } as const)
              : ({ kind: "bom" } as const);
        return <JobWorkspace jobId={id} view={view} onNavigate={navigate} />;
      }
      return (
        <DrawingsPage
          view={sub}
          onOpen={(jobId) => navigate("drawings", jobId)}
          onOpenPart={(jobId, itemNo) => navigate("drawings", jobId, itemNo)}
          onNavigate={navigate}
        />
      );
    }
    if (page === "rfq") {
      // The front door: mail + files in, an RFQ with its journey under one strip.
      if (id === "new") return <RfqIntakePage onNavigate={navigate} />;
      return id ? (
        <RfqDetailPage rfqId={id} step={sub} onNavigate={navigate} />
      ) : (
        <RfqPage onNavigate={navigate} />
      );
    }
    if (page === "customers") {
      return id ? (
        <CustomerDetailPage customerId={id} onNavigate={navigate} />
      ) : (
        <CustomersPage onNavigate={navigate} />
      );
    }
    if (page === "ai") return <AiPage onNavigate={navigate} />;
    if (page === "erp") return <ErpPage onNavigate={navigate} />;
    if (page === "production") {
      return id ? (
        <ProductionDetailPage workOrderId={id} onNavigate={navigate} />
      ) : (
        <ProductionPage onNavigate={navigate} />
      );
    }
    if (page === "profile") return <ProfilePage section={id} onNavigate={navigate} />;
    if (page === "agents") return <AgentsPage onNavigate={navigate} />;
    if (page === "integrations") return <IntegrationsPage onNavigate={navigate} />;
    return <NotFoundPage onNavigate={navigate} />;
  }

  return (
    // v2 is a light design; pin reused v1 components (the CAD viewer and its
    // scene, renderer and toolbar) to light without touching the stored preference.
    <ThemeOverrideProvider theme="light">
      <AlertsProvider>
        <ShellContext.Provider value={shellState}>
          {booted ? null : <Splash />}
          <div className={ui.shell} hidden={!booted}>
            <div
              data-theme="light"
              className="flex h-full items-stretch overflow-hidden bg-fixed font-ds text-ds-ink"
              style={settingsPageStyle}
              data-sidebar={narrow ? (sidebarOpen ? "open" : "closed") : "open"}
              data-narrow={narrow ? "true" : undefined}
            >
              <Sidebar
                current={route.page}
                onNavigate={navigate}
                onToggleCollapse={toggleSidebar}
              />
              {narrow && drawerOpen ? (
                <button
                  type="button"
                  className="fixed inset-0 z-40 cursor-pointer border-0 bg-[rgb(11_18_17/0.4)] p-0"
                  aria-label="Close menu"
                  data-testid="scrim"
                  onClick={closeSidebar}
                />
              ) : null}
              <main className={ui.main} ref={mainRef}>
                {/* Chunks are prefetched after first paint; the skeleton only shows on a cold, slow link. */}
                <Suspense fallback={<SkeletonRows rows={8} />}>{renderPage()}</Suspense>
              </main>
            </div>
          </div>
        </ShellContext.Provider>
      </AlertsProvider>
    </ThemeOverrideProvider>
  );
}
